import { supabase } from '../../shared/supabase/supabaseClient';
import {
  normalizeBusinessTypeListRaw,
  parseBusinessTypeList,
  type BusinessTypeListItem,
} from '../../shell/session/domain/businessProfile';
import { fetchBusinessTypes } from './businessTypeService';
import {
  DEFAULT_OTHER_FEATURE_GROUPS,
  parseOtherFeatureGroups,
  type OtherFeatureGroup,
} from './otherFeatureCatalog';
import { isOnboardingBusinessTypesFullListEnabled } from './onboardingBusinessTypeVisibility';

/** Client-side QSF expand when RPC does not emit flavor tiles yet. */
const QSF_FLAVOR_TILES = [
  { flavor: 'pizzeria', packCode: 'qsf_pizzeria_ar_v1', label: 'Pizzería', description: 'Pizzas, empanadas, combos.', icon: '🍕', keywords: ['pizzería', 'pizza', 'muzza', 'empanadas'] },
  { flavor: 'burger', packCode: 'qsf_burger_ar_v1', label: 'Hamburguesería', description: 'Burgers, papas, combos.', icon: '🍔', keywords: ['hamburguesería', 'hamburguesa', 'burger', 'papas'] },
  { flavor: 'parrilla', packCode: 'qsf_parrilla_ar_v1', label: 'Parrilla', description: 'Carnes y guarniciones.', icon: '🥩', keywords: ['parrilla', 'asado', 'carnes', 'bbq'] },
  { flavor: 'cafe', packCode: 'qsf_cafe_ar_v1', label: 'Café', description: 'Café, medialunas, sandwiches.', icon: '☕', keywords: ['café', 'cafetería', 'coffee', 'merienda'] },
] as const;

/**
 * Same ERP (`weight_retail`) as two picker tiles so carniceros find themselves.
 * Flavors are identity only — same capabilities, no catalog pack apply in alta.
 */
const WEIGHT_RETAIL_FLAVOR_TILES = [
  {
    flavor: 'verduleria',
    label: 'Verdulería',
    description: 'Fruta y verdura por kilo.',
    icon: '🥬',
    keywords: ['frutería', 'granja', 'verduras', 'frutas'],
  },
  {
    flavor: 'carniceria',
    label: 'Carnicería',
    description: 'Cortes, pollo y fiambres por kilo.',
    icon: '🥩',
    keywords: ['carnicería', 'pollería', 'carne', 'fiambres'],
  },
] as const;

export type OnboardingPickerItem = BusinessTypeListItem & {
  /** Unique key when the same ERP code appears as several tiles (QSF flavors). */
  picker_key: string;
  vertical_flavor: string | null;
  picker_icon: string | null;
  picker_keywords: string[];
};

export type OnboardingPickerSection = {
  id: string;
  label: string;
  sort_order: number;
  items: OnboardingPickerItem[];
};

export type OnboardingPickerPayload = {
  sections: OnboardingPickerSection[];
  /** Flat items for search / legacy callers. */
  items: OnboardingPickerItem[];
  other_code: string;
  other_feature_groups: OtherFeatureGroup[];
};

function expandQsfItem(base: BusinessTypeListItem): OnboardingPickerItem[] {
  return QSF_FLAVOR_TILES.map((flavor, index) => ({
    ...base,
    display_name: flavor.label,
    description: flavor.description,
    onboarding_pack_code: flavor.packCode,
    sort_order: base.sort_order + index * 0.01,
    picker_key: `${base.code}:${flavor.flavor}`,
    vertical_flavor: flavor.flavor,
    picker_icon: flavor.icon,
    picker_keywords: [...flavor.keywords],
  }));
}

function expandWeightRetailItem(base: BusinessTypeListItem): OnboardingPickerItem[] {
  return WEIGHT_RETAIL_FLAVOR_TILES.map((flavor, index) => ({
    ...base,
    display_name: flavor.label,
    description: flavor.description,
    // Alta proto v2 does not apply packs; keep ERP pack code for change-rubro / future opt-in.
    onboarding_pack_code: base.onboarding_pack_code,
    sort_order: base.sort_order + index * 0.01,
    picker_key: `${base.code}:${flavor.flavor}`,
    vertical_flavor: flavor.flavor,
    picker_icon: flavor.icon,
    picker_keywords: [...flavor.keywords],
  }));
}

/** Expand QSF + weight_retail into flavor tiles; keep other types 1:1. */
export function expandBusinessTypesToPickerItems(
  types: BusinessTypeListItem[],
): OnboardingPickerItem[] {
  const out: OnboardingPickerItem[] = [];
  for (const type of types) {
    if (type.code === 'quick_service_food') {
      out.push(...expandQsfItem(type));
      continue;
    }
    if (type.code === 'weight_retail') {
      out.push(...expandWeightRetailItem(type));
      continue;
    }
    out.push({
      ...type,
      picker_key: type.code,
      vertical_flavor: null,
      picker_icon: type.picker_icon ?? null,
      picker_keywords: type.picker_keywords ?? [],
    });
  }
  return out;
}

function sectionIdFromGroup(group: string | null): string {
  if (!group) return 'otros';
  return group
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function groupPickerItemsIntoSections(items: OnboardingPickerItem[]): OnboardingPickerSection[] {
  const byGroup = new Map<string | null, OnboardingPickerItem[]>();
  for (const item of items) {
    const key = item.onboarding_picker_group;
    const bucket = byGroup.get(key) ?? [];
    bucket.push(item);
    byGroup.set(key, bucket);
  }
  const sections: OnboardingPickerSection[] = [];
  let sort = 0;
  for (const item of items) {
    const key = item.onboarding_picker_group;
    if (sections.some((s) => s.label === (key ?? ''))) continue;
    const groupItems = byGroup.get(key) ?? [];
    sections.push({
      id: sectionIdFromGroup(key),
      label: key ?? 'Otros',
      sort_order: sort++,
      items: groupItems,
    });
  }
  return sections;
}

function buildPayloadFromTypes(types: BusinessTypeListItem[]): OnboardingPickerPayload {
  const items = expandBusinessTypesToPickerItems(types);
  return {
    sections: groupPickerItemsIntoSections(items),
    items,
    other_code: 'general_retail',
    other_feature_groups: [...DEFAULT_OTHER_FEATURE_GROUPS],
  };
}

function parseRpcPayload(raw: unknown): OnboardingPickerPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.sections) && o.sections.length > 0) {
    const sections: OnboardingPickerSection[] = [];
    const flat: OnboardingPickerItem[] = [];
    for (const s of o.sections) {
      if (!s || typeof s !== 'object') continue;
      const so = s as Record<string, unknown>;
      const label = typeof so.label === 'string' ? so.label : null;
      if (!label || !Array.isArray(so.items)) continue;
      const items: OnboardingPickerItem[] = [];
      for (const it of so.items) {
        if (!it || typeof it !== 'object') continue;
        const io = it as Record<string, unknown>;
        if (typeof io.code !== 'string' || typeof io.display_name !== 'string') continue;
        const flavor =
          typeof io.vertical_flavor === 'string' && io.vertical_flavor.trim()
            ? io.vertical_flavor.trim()
            : null;
        const item: OnboardingPickerItem = {
          code: io.code as BusinessTypeListItem['code'],
          display_name: io.display_name,
          description: typeof io.description === 'string' ? io.description : null,
          sort_order: Number(io.sort_order ?? 0),
          onboarding_pack_code:
            typeof io.onboarding_pack_code === 'string' ? io.onboarding_pack_code : null,
          onboarding_picker_group: label,
          picker_key:
            typeof io.picker_key === 'string'
              ? io.picker_key
              : flavor
                ? `${io.code}:${flavor}`
                : io.code,
          vertical_flavor: flavor,
          picker_icon: typeof io.picker_icon === 'string' ? io.picker_icon : null,
          picker_keywords: Array.isArray(io.picker_keywords)
            ? io.picker_keywords.filter((k): k is string => typeof k === 'string')
            : [],
        };
        items.push(item);
        flat.push(item);
      }
      if (items.length > 0) {
        sections.push({
          id: typeof so.id === 'string' ? so.id : sectionIdFromGroup(label),
          label,
          sort_order: Number(so.sort_order ?? sections.length),
          items,
        });
      }
    }
    if (sections.length === 0) return null;
    return {
      sections,
      items: flat,
      other_code: typeof o.other_code === 'string' ? o.other_code : 'general_retail',
      other_feature_groups: parseOtherFeatureGroups(o.other_feature_groups),
    };
  }

  // Flat list fallback shape from older RPC drafts
  const list = parseBusinessTypeList(normalizeBusinessTypeListRaw(o.items ?? o));
  if (list.length === 0) return null;
  const built = buildPayloadFromTypes(list);
  return {
    ...built,
    other_feature_groups: parseOtherFeatureGroups(o.other_feature_groups),
  };
}

/**
 * Prefer `list_onboarding_picker` (sections + flavors + Otros features).
 * Falls back to `list_business_types` + client expand.
 */
export async function fetchOnboardingPicker(countryCode = 'AR'): Promise<OnboardingPickerPayload> {
  const { data, error } = await supabase.rpc('list_onboarding_picker', {
    p_country_code: countryCode,
    p_include_all: isOnboardingBusinessTypesFullListEnabled(),
  });

  if (!error && data != null) {
    const parsed = parseRpcPayload(data);
    if (parsed && parsed.items.length > 0) return parsed;
  } else if (error) {
    console.warn('[Onboarding] list_onboarding_picker', error.message);
  }

  const types = await fetchBusinessTypes(countryCode);
  return buildPayloadFromTypes(types);
}
